/* Design Studio — a faithful React port of the prototype's rDesign screen. */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useRef, useState } from 'react';
import {
  Engine, COMPONENTS, SEGMENTS, TECH_BY_ID, QUARTER_NAMES, fmtM, fmt$,
} from '@venturewrist/engine';
import type { Segment } from '@venturewrist/shared';
import { useDecisions } from '../../store/decisions';
import { buildWorkingGame, newProductIndex, NEW_PREFIX } from '../../lib/designState';
import { tutSignal } from '../../tutorial/useTutorial';
import WatchViewer from '../../components/WatchViewer';
import { Select } from '../../components/ui';
import type { ScreenProps } from './types';
import '../../styles/design-studio.css';

const Eng = Engine as any;
const C = COMPONENTS as any;
const SEGS = SEGMENTS as any[];
const TECH = TECH_BY_ID as Record<string, any>;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const hex = (n: number) => '#' + (n >>> 0).toString(16).padStart(6, '0').slice(-6);
const segById = (id: string) => SEGS.find((s) => s.id === id);
const cloneSpec = (s: any): any => ({ ...s, sensors: [...(s.sensors || [])] });

const WhyBtn = () => <span className="tooltip-why" title="Learn the theory">?</span>;
const Bar = ({ v }: { v: number }) => (
  <div className="bar-track"><div className="bar-fill" style={{ width: clamp(v, 0, 100) + '%' }} /></div>
);

export function Products({ detail }: ScreenProps) {
  const draft = useDecisions((z) => z.draft);
  const addNewProduct = useDecisions((z) => z.addNewProduct);
  const removeNewProduct = useDecisions((z) => z.removeNewProduct);
  const updateNewProduct = useDecisions((z) => z.updateNewProduct);
  const setProductName = useDecisions((z) => z.setProductName);
  const setTargetSegStore = useDecisions((z) => z.setTargetSeg);
  const setRetire = useDecisions((z) => z.setRetire);
  const setGenSpec = useDecisions((z) => z.setGenSpec);
  const setNewGen = useDecisions((z) => z.setNewGen);

  const wg = useMemo(() => buildWorkingGame(detail.state, draft), [detail.state, draft]);
  const models = wg.products.filter((x: any) => x.active);

  const [designProd, setDesignProd] = useState<string | null>(null);
  const [designGen, setDesignGen] = useState<number | 'draft' | null>(null);
  const [hudSegMap, setHudSegMap] = useState<Record<string, string>>({});
  const [localDraft, setLocalDraft] = useState<{ productId: string; base: number; spec: any } | null>(null);
  const [newModal, setNewModal] = useState(false);
  const fitRef = useRef<{ ctx: string | null; prev: number | null }>({ ctx: null, prev: null });

  const createProduct = (seg: string) => {
    const idx = draft.newProducts?.length ?? 0;
    addNewProduct({ seg: seg as Segment });
    setDesignProd(NEW_PREFIX + idx);
    setDesignGen(null);
    setLocalDraft(null);
    setNewModal(false);
  };

  if (!models.length) {
    return (
      <div className="ds-root">
        <div className="empty-state">
          <div className="ic">🎨</div>
          <h3 style={{ color: 'var(--txt)', textTransform: 'none', fontSize: 17 }}>No products yet</h3>
          <button className="btn btn-pri" style={{ marginTop: 14 }} data-tut="design-segment" onClick={() => setNewModal(true)}>＋ Design your first watch</button>
        </div>
        {newModal && <NewModelModal onPick={createProduct} onClose={() => setNewModal(false)} />}
      </div>
    );
  }

  const m = models.find((p: any) => p.id === designProd) ?? models[0];
  const hasLocalDraft = !!localDraft && localDraft.productId === m.id;

  let sel: number | 'draft' | null = designGen;
  if (sel === 'draft' && !hasLocalDraft) sel = null;
  if (sel != null && sel !== 'draft') {
    const x = m.gens.find((v: any) => v.gen === sel);
    if (!x || Eng.genStatus(m, x) === 'retired') sel = null;
  }
  if (sel == null) sel = hasLocalDraft ? 'draft' : Eng.currentGen(m).gen;

  const isDraft = sel === 'draft';
  const gv = isDraft ? null : m.gens.find((x: any) => x.gen === sel);
  const spec = isDraft ? localDraft!.spec : gv.spec;
  const status = isDraft ? null : Eng.genStatus(m, gv);
  const dev = status === 'development';
  const proto = !isDraft && gv && gv.proto === wg.round;
  const editable = isDraft || proto;
  const isNew = newProductIndex(m.id) !== null;
  const newIdx = newProductIndex(m.id);

  const hud = hudSegMap[m.id] || m.targetSeg;
  const hudSegObj = segById(hud);
  const a = Eng.attrs(spec, wg);
  const fit = Eng.segFit(spec, a, hud, wg);

  const fitKey = m.id + '|' + sel + '|' + hud;
  let delta: number | null = null;
  if (fitRef.current.ctx === fitKey && fitRef.current.prev != null && Math.abs(fit - fitRef.current.prev) >= 0.5)
    delta = fit - fitRef.current.prev;
  fitRef.current = { ctx: fitKey, prev: fit };

  const refQty = (isDraft || !gv.production) ? (Eng.currentGen(m).production || 40) : gv.production;
  const uc = Eng.unitCost(spec, wg, refQty);
  const price = isDraft ? ((Eng.currentGen(m).price || 199) + 30) : gv.price;
  const devG = Eng.devGen(m);
  const rndDone: string[] = wg.rnd?.done ?? [];

  const applySpec = (newSpec: any) => {
    if (isDraft) { setLocalDraft((d) => (d ? { ...d, spec: newSpec } : d)); return; }
    if (isNew && newIdx != null) { updateNewProduct(newIdx, { spec: newSpec }); return; }
    setGenSpec(m.id, sel as number, newSpec);
  };
  const setField = (f: string, val: any) => { tutSignal('design-edit'); applySpec({ ...cloneSpec(spec), [f]: val }); };
  const toggleSensor = (id: string) => {
    tutSignal('design-edit');
    const cur: string[] = spec.sensors || [];
    const sensors = cur.includes(id)
      ? (cur.length > 1 ? cur.filter((x) => x !== id) : cur)
      : [...cur, id];
    applySpec({ ...cloneSpec(spec), sensors });
  };
  const renameModel = (v: string) => {
    if (!v.trim()) return;
    if (isNew && newIdx != null) updateNewProduct(newIdx, { name: v.trim() });
    else setProductName(m.id, v.trim());
  };
  const setHudSeg = (id: string) => { if (segById(id)) setHudSegMap((x) => ({ ...x, [m.id]: id })); };
  const makeTarget = () => {
    const seg = (hudSegMap[m.id] || m.targetSeg) as Segment;
    // `seg` is the template the product was seeded from (spec + launch price);
    // retargeting only moves the aim, as the prototype's setTargetSeg does.
    if (isNew && newIdx != null) updateNewProduct(newIdx, { targetSeg: seg });
    else setTargetSegStore(m.id, seg);
  };
  const selModel = (id: string) => { setDesignProd(id); setDesignGen(null); };
  const selGen = (v: number | 'draft') => setDesignGen(v);
  const startNewGen = () => {
    const cur = Eng.currentGen(m);
    setLocalDraft({ productId: m.id, base: cur.gen, spec: cloneSpec(cur.spec) });
    setDesignGen('draft');
  };
  const commitGen = () => {
    if (!localDraft) return;
    setNewGen(m.id, { base: localDraft.base, spec: localDraft.spec });
    setDesignGen(m.nextGen);
    setLocalDraft(null);
  };
  const discardGen = () => { setLocalDraft(null); setDesignGen(null); };
  const retire = () => {
    if (models.length <= 1) { window.alert('You need at least one active product.'); return; }
    if (!window.confirm('Retire the entire ' + m.name + ' line? All its generations stop selling; drafts are discarded.')) return;
    if (isNew && newIdx != null) removeNewProduct(newIdx);
    else setRetire(m.id, true);
    setDesignProd(null);
    setDesignGen(null);
    setLocalDraft(null);
  };

  const hintD = (s2: any) => Eng.segFit(s2, Eng.attrs(s2, wg), hud, wg) - fit;
  const swap = (f: string, id: string) => { const s2 = cloneSpec(spec); s2[f] = id; return s2; };
  // Rendered as plain function calls (not <Badge/>/<Pill/> components) so React
  // reconciles them in place. Defining components inside render gives them a new
  // identity each render, which remounts the whole section on every edit — that
  // remount was destroying the browser's scroll anchor and making the page jump.
  const badge = (d: number) =>
    Math.abs(d) < 0.5 ? <span className="fit-nil">±0 fit</span>
      : d > 0 ? <span className="fit-up">▲ +{Math.round(d)} fit</span>
        : <span className="fit-dn">▼ −{Math.abs(Math.round(d))} fit</span>;
  const lockTip = (c: any) => { const t = TECH[c.lock]; return t ? 'Requires research: ' + t.icon + ' ' + t.name : 'Requires R&D'; };
  const tip = 'Fit for ' + hudSegObj.name + ' if swapped in. Fit only — weigh it against the cost and attribute effects shown alongside.';

  const pill = (cat: string, f: string, label: string, fmt: (c: any) => string, tut?: string) => (
    <div className="comp-group" data-tut={tut}>
      <div className="g-head">{label}{!editable && <span className="sub">🔒 frozen</span>}</div>
      <div className="opt-row">
        {C[cat].map((c: any) => {
          const on = spec[f] === c.id;
          const rdLocked = c.lock && !rndDone.includes(c.lock);
          const cls = 'opt-pill ' + (on ? 'on ' : '') + (rdLocked ? 'locked' : (!editable ? 'frozen' : ''));
          const onClick = rdLocked ? undefined : editable ? () => setField(f, c.id) : undefined;
          return (
            <button key={c.id} className={cls} title={rdLocked ? lockTip(c) : tip} onClick={onClick}>
              {rdLocked && <span className="lk">🔒</span>}{c.name}{!editable && on ? ' 🔒' : ''}
              <span className="pc">{fmt(c)}{rdLocked && <> · <span className="rd">R&amp;D</span></>}</span>
              {!on && badge(hintD(swap(f, c.id)))}
            </button>
          );
        })}
      </div>
    </div>
  );

  const targetSeg = segById(m.targetSeg);

  const draftModel = isDraft ? (() => { const mm = structuredClone(m); mm.draft = { base: localDraft!.base, spec: localDraft!.spec }; return mm; })() : null;
  const chs: any[] = draftModel ? Eng.draftChanges(draftModel) : [];
  const cost = draftModel ? Eng.draftCost(draftModel, wg) : 0;
  const baseGen = isDraft ? m.gens.find((x: any) => x.gen === localDraft!.base) : null;
  const baseFit = baseGen ? Eng.segFit(baseGen.spec, Eng.attrs(baseGen.spec, wg), hud, wg) : 0;
  const capHit = Eng.prodGens(m).length >= Eng.genCap(wg);
  const oldest = capHit ? Eng.prodGens(m).reduce((x: any, y: any) => (x.gen < y.gen ? x : y)) : null;
  const compName = (f: string, id: string) =>
    f === 'size' ? C.sizes.find((c: any) => c.id === id)?.name
      : f === 'style' ? C.style.find((c: any) => c.id === id)?.name
        : f === 'color' ? C.colors.find((c: any) => c.id === id)?.name
          : Eng.comp(f === 'casing' ? 'casing' : f, id)?.name;
  const compCost = (f: string, id: string) => { const c = (f === 'size' || f === 'style' || f === 'color') ? null : Eng.comp(f, id); return c ? c.cost : 0; };

  return (
    <div className="ds-root">
      <div className="screen-head">
        <div className="spread">
          <div>
            <h2>🎨 Design Studio<WhyBtn /></h2>
            <div className="sub">A model is your product line; a generation is a frozen version of its hardware<WhyBtn /></div>
          </div>
          <div className="row" style={{ flexWrap: 'wrap' }}>
            {models.map((x: any) => (
              <button key={x.id} className={'opt-pill ' + (x.id === m.id ? 'on' : '')} onClick={() => selModel(x.id)}>{x.name}</button>
            ))}
            {models.length < 4 && <button className="btn btn-ghost btn-sm" data-tut="design-segment" onClick={() => setNewModal(true)}>＋ New model</button>}
          </div>
        </div>
        <div className="gen-strip">
          {m.gens.filter((x: any) => Eng.genStatus(m, x) !== 'retired').map((x: any) => {
            const st = Eng.genStatus(m, x); const S = Eng.STATUS[st];
            return (
              <button key={x.gen} className={'opt-pill ' + (!isDraft && sel === x.gen ? 'on' : '')} onClick={() => selGen(x.gen)}>
                {st === 'development'
                  ? <>🔧 Gen {x.gen} — launches {QUARTER_NAMES(wg.round + 1)}</>
                  : <>Gen {x.gen} <span style={{ color: S.color }}>{S.icon} {S.label}</span></>}
              </button>
            );
          })}
          {hasLocalDraft && <button className={'opt-pill draftpill ' + (isDraft ? 'on' : '')} onClick={() => selGen('draft')}>✏️ Gen {m.nextGen} — draft</button>}
          {!hasLocalDraft && !devG && !isNew && <button className="btn btn-ghost btn-sm" onClick={startNewGen}>＋ New generation</button>}
          {devG && !hasLocalDraft && <span className="sub">engineering is busy until Gen {devG.gen} launches</span>}
        </div>
      </div>

      <div className="studio">
        <div className={'viewer3d' + (isDraft ? ' draftmode' : '')} data-tut="design-viewer">
          <WatchViewer spec={{ casing: spec.casing, strap: spec.strap, size: spec.size, style: spec.style, display: spec.display, color: spec.color, sensors: spec.sensors }} brand={m.id} />
          {isDraft && <div className="draft-wm">DRAFT</div>}
          <div className="viewer-hud">
            <span className="chip acc">{m.name} · {isDraft ? `Gen ${m.nextGen} DRAFT` : `Gen ${sel}${dev ? ' 🔧' : ''}`}</span>
            <div className="hud-card" title={hudSegObj.desc + ' — preview any segment without retargeting.'}>
              <span className="l">Scoring for</span><br />
              <Select size="sm" className="mt-0.5 font-bold" aria-label="Scoring segment" value={hud} onChange={(e) => setHudSeg(e.target.value)}>
                {SEGS.map((s) => <option key={s.id} value={s.id}>{s.icon} {s.name}</option>)}
              </Select>
            </div>
            {hud !== m.targetSeg && <button className="chip" style={{ cursor: 'pointer' }} onClick={makeTarget} title={`Make ${hudSegObj.name} this model's target segment`}>◎ make target</button>}
            <div className="hud-card">
              <span className="l">Segment fit</span>
              <div style={{ fontSize: 17, fontWeight: 800 }}>{Math.round(fit)}<span className="sub" style={{ fontWeight: 600 }}> /100</span>
                {delta != null && <span className={'fit-delta ' + (delta > 0 ? 'pos' : 'neg')}>{delta > 0 ? '▲ +' : '▼ −'}{Math.abs(Math.round(delta))}</span>}
              </div>
            </div>
          </div>
          <div className="spec-strip">
            <div className="spec-pill"><div className="l">Unit cost @ {refQty}k</div><div className="v">{fmt$(uc)}</div></div>
            <div className="spec-pill"><div className="l">{isDraft ? 'Est. launch price' : 'Price · Margin'}</div>
              <div className="v">{fmt$(price)}{!isDraft && <> · <span className={price - uc > 0 ? 'pos' : 'neg'}>{Math.round((price - uc) / price * 100)}%</span></>}</div></div>
            <div className="spec-pill"><div className="l">Battery life</div><div className="v">{Math.round(a.hours)} h</div></div>
            <div className="spec-pill"><div className="l">Weight</div><div className="v">{Math.round(a.weight)} g</div></div>
            <div className="spec-pill"><div className="l">Quality index</div><div className="v">{Math.round(a.quality)}</div></div>
          </div>
          <div className="viewer-hint">drag to rotate · {editable ? 'design updates live' : 'hardware frozen at launch'}</div>
        </div>

        <div className="design-panel">
          {isDraft ? (
            <>
              <div className="comp-group" style={{ borderColor: 'rgb(var(--c-warn) / .4)' }}>
                <div className="g-head">Changes vs Gen {baseGen.gen}<span className="sub">{chs.length || 'no'} change{chs.length === 1 ? '' : 's'}</span></div>
                {chs.length ? chs.map((c: any, i: number) => {
                  if (c.f === 'sensors') {
                    const add = spec.sensors.filter((x: string) => !baseGen.spec.sensors.includes(x)).map((x: string) => '+ ' + Eng.comp('sensors', x).name);
                    const rem = baseGen.spec.sensors.filter((x: string) => !spec.sensors.includes(x)).map((x: string) => '− ' + Eng.comp('sensors', x).name);
                    const d = spec.sensors.reduce((s2: number, x: string) => s2 + Eng.comp('sensors', x).cost, 0) - baseGen.spec.sensors.reduce((s2: number, x: string) => s2 + Eng.comp('sensors', x).cost, 0);
                    return <div className="diffrow" key={i}><b>Sensors</b><span>{add.concat(rem).join(', ')}</span><span className="sub">{d ? 'unit cost ' + (d > 0 ? '+' : '−') + '$' + Math.abs(d) : '—'}</span></div>;
                  }
                  const d = compCost(c.f, spec[c.f]) - compCost(c.f, baseGen.spec[c.f]);
                  return <div className="diffrow" key={i}><b>{c.label}</b><span>{compName(c.f, baseGen.spec[c.f])} → {compName(c.f, spec[c.f])}</span><span className="sub">{d ? 'unit cost ' + (d > 0 ? '+' : '−') + '$' + Math.abs(d) : '—'}</span></div>;
                }) : <div className="sub">Identical to Gen {baseGen.gen} — change at least one component to engineer a new generation.</div>}
                <div className="spread" style={{ marginTop: 8, fontSize: 12 }}><span>Segment fit · {hudSegObj.name}</span><b>{Math.round(baseFit)} → <span style={{ color: fit >= baseFit ? 'var(--good)' : 'var(--bad)' }}>{Math.round(fit)}</span></b></div>
              </div>
              <div className="comp-group" style={{ borderColor: 'rgb(var(--c-warn) / .4)' }}>
                <div className="g-head">Engineering quote</div>
                <div className="grid2" style={{ gap: 8 }}>
                  <div><div className="sub">Engineering cost</div><b style={{ fontSize: 16 }}>{chs.length ? fmtM(cost) : '—'}</b></div>
                  <div><div className="sub">Launches</div><b style={{ fontSize: 16 }}>{QUARTER_NAMES(wg.round + 1)}</b><div className="sub">next quarter</div></div>
                </div>
                <div className="sub" style={{ margin: '8px 0' }}>$0.5M base + $0.25M per changed component (finish-only: flat $0.3M), capped at $2.5M — charged at advance. Launch defaults: current gen's production volume, its price + $30.</div>
                <div className="row" style={{ flexWrap: 'wrap' }}>
                  <button className="btn btn-pri btn-sm" disabled={!chs.length || wg.cash < cost} onClick={commitGen}>
                    {capHit ? 'Commit — ' + Eng.genName(m, oldest) + ' winds down at launch' : 'Commit — start engineering'}
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={discardGen}>Discard draft</button>
                </div>
                {!!chs.length && wg.cash < cost && <div className="sub" style={{ color: 'var(--bad)', marginTop: 6 }}>Not enough cash ({fmtM(wg.cash)} on hand).</div>}
              </div>
            </>
          ) : (
            <div className="freeze-banner" style={dev ? undefined : proto ? { borderColor: 'rgb(var(--c-good) / .45)', color: 'var(--good)' } : undefined}>
              {dev
                ? <>🔧 Gen {sel} is locked in engineering — launches {QUARTER_NAMES(wg.round + 1)}. Its spec is already frozen.</>
                : proto
                  ? <>🧪 New model — customize its components freely. Hardware freezes when {QUARTER_NAMES(wg.round)} ends and Gen 1 ships.<WhyBtn /></>
                  : <>🔒 Shipped hardware is frozen. To change components, start a new generation.<WhyBtn /></>}
            </div>
          )}

          <div className="comp-group" data-tut="design-commit">
            <div className="g-head">Model name<WhyBtn /></div>
            <div className="row"><input style={{ flex: 1 }} defaultValue={m.name} key={m.id + m.name} onBlur={(e) => renameModel(e.target.value)} /></div>
            <div className="sub" style={{ marginTop: 7 }}>Generations are named automatically ("{m.name} 2"). Target: {targetSeg.icon} {targetSeg.name} — preview &amp; retarget via the pill on the 3D viewer.</div>
          </div>

          {pill('cpu', 'cpu', 'Processor', (c) => `$${c.cost} · perf ${c.perf}`, 'design-components')}
          {pill('display', 'display', 'Display', (c) => `$${c.cost} · quality ${c.quality}`)}
          {pill('battery', 'battery', 'Battery', (c) => `$${c.cost} · ${c.cap} mAh · ${c.weight}g`)}

          <div className="comp-group">
            <div className="g-head">Sensors <span className="sub">{spec.sensors.length} fitted{!editable && ' · 🔒 frozen'}</span></div>
            <div className="opt-row">
              {C.sensors.map((c: any) => {
                const on = spec.sensors.includes(c.id);
                const rdLocked = c.lock && !rndDone.includes(c.lock);
                const s2 = { ...cloneSpec(spec), sensors: on ? spec.sensors.filter((x: string) => x !== c.id) : [...spec.sensors, c.id] };
                const cls = 'opt-pill ' + (on ? 'on ' : '') + (rdLocked ? 'locked' : (!editable ? 'frozen' : ''));
                return (
                  <button key={c.id} className={cls} title={rdLocked ? lockTip(c) : tip} onClick={rdLocked ? undefined : editable ? () => toggleSensor(c.id) : undefined}>
                    {rdLocked && <span className="lk">🔒</span>}{c.name}{!editable && on ? ' 🔒' : ''}
                    <span className="pc">${c.cost} · health +{c.health}{rdLocked && <> · <span className="rd">R&amp;D</span></>}</span>
                    {badge(hintD(s2))}
                  </button>
                );
              })}
            </div>
          </div>

          {pill('casing', 'casing', 'Case material', (c) => `$${c.cost} · style ${c.fashion} · ${c.weight}g`)}
          {pill('strap', 'strap', 'Strap', (c) => `$${c.cost} · style ${c.fashion}`)}

          <div className="comp-group">
            <div className="g-head">Size &amp; design language{!editable && <span className="sub">🔒 frozen</span>}</div>
            <div className="opt-row" style={{ marginBottom: 8 }}>
              {C.sizes.map((c: any) => {
                const on = spec.size === c.id;
                return <button key={c.id} className={'opt-pill ' + (on ? 'on ' : '') + (!editable ? 'frozen' : '')} title={tip} onClick={editable ? () => setField('size', c.id) : undefined}>{c.name}{!on && badge(hintD(swap('size', c.id)))}</button>;
              })}
            </div>
            <div className="opt-row">
              {C.style.map((c: any) => {
                const on = spec.style === c.id;
                return <button key={c.id} className={'opt-pill ' + (on ? 'on ' : '') + (!editable ? 'frozen' : '')} title={tip} onClick={editable ? () => setField('style', c.id) : undefined}>{c.name}<span className="pc">{c.desc}</span>{!on && badge(hintD(swap('style', c.id)))}</button>;
              })}
            </div>
          </div>

          <div className="comp-group">
            <div className="g-head">Finish{!editable && <span className="sub">🔒 frozen</span>}</div>
            <div className="swatches">
              {C.colors.map((c: any) => (
                <div key={c.id} className={'swatch ' + (spec.color === c.id ? 'on' : '')} style={{ background: hex(c.hex), opacity: editable ? 1 : 0.6 }} title={c.name} onClick={editable ? () => setField('color', c.id) : undefined} />
              ))}
            </div>
          </div>

          <div className="comp-group">
            <div className="g-head">Attribute profile</div>
            <div className="attr-bars">
              {([['Performance', a.perf], ['Battery', a.bat], ['Health', a.health], ['Style', a.fashion], ['Durability', a.durab], ['Comfort', a.comfort]] as [string, number][]).map(([l, v]) => (
                <div className="attr-bar" key={l}><div className="spread"><span>{l}</span><b>{Math.round(v)}</b></div><Bar v={v} /></div>
              ))}
            </div>
            <button className="btn btn-danger btn-sm" style={{ marginTop: 12 }} onClick={retire}>Retire this model</button>
          </div>
        </div>
      </div>

      {newModal && <NewModelModal onPick={createProduct} onClose={() => setNewModal(false)} />}
    </div>
  );
}

function NewModelModal({ onPick, onClose }: { onPick: (seg: string) => void; onClose: () => void }) {
  return (
    <div className="ds-root" style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'grid', placeItems: 'center', background: 'var(--scrim)', backdropFilter: 'blur(3px)' }} onClick={onClose}>
      <div className="card" style={{ width: 'min(680px,92vw)', maxHeight: '86vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 style={{ fontSize: 18, fontWeight: 800 }}>New Product Line</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <p className="sub" style={{ marginBottom: 12 }}>Pick the target segment — we will draft a sensible starting design you can refine. Each model adds $300k/qtr overhead (+$100k/qtr per extra in-production generation).</p>
        <div className="grid2" data-tut="design-segment">
          {SEGS.map((s) => (
            <div key={s.id} className="card" style={{ cursor: 'pointer' }} onClick={() => onPick(s.id)}>
              <div className="row"><span style={{ fontSize: 20 }}>{s.icon}</span><b>{s.name}</b></div>
              <div className="sub" style={{ marginTop: 5 }}>{s.desc}</div>
              <div className="sub" style={{ marginTop: 5, color: s.color }}>Ref price {fmt$(s.refPrice)} · concerns 💲{s.concerns.pc}% ⚙️{s.concerns.qc}% 🏷️{s.concerns.bc}%</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
